import { useFeatureGate } from '@statsig/react-bindings';
import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  CoachConversation,
  INTAKE_SUMMARY_SOURCE,
  ProviderEventType,
  SchedulePreviewAppointment,
  UpcomingAgendaCardConnected,
  UpcomingAgendaErrorCard,
  useSetCoachConversation,
} from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { showKaiserIntakeCSSRSGate } from '../../statsig/gates';
import { IntakeSummaryConnectedWithFormsPrefetch } from '../client-information/IntakeSummaryConnectedWithFormsPrefetch';
import { PreVisitSummaryCardNudgeWrapper } from '../client-information/pre-visit-summary/PreVisitSummaryCardNudgeWrapper';
import { useExternalResourceButtonOnClickHandlers } from '../client-information/pre-visit-summary/useExternalResourceButtonOnClickHandlers';
import { useOpenClientInfoDrawerForAppointment } from '../client-information/useOpenClientInfoDrawerForAppointment';
import { useOpenIntakeFormResponseCallback } from '../client-information/useOpenIntakeFormResponseCallback';
import { AppointmentType } from '../schedule-preview/types';

export function UpcomingAgendaWrapper() {
  const { userClicked } = useTrackEvent();
  const { value: kaiserGate } = useFeatureGate(showKaiserIntakeCSSRSGate);
  const openClientInfoDrawerForAppointment = useOpenClientInfoDrawerForAppointment();
  const openIntakeFormResponse = useOpenIntakeFormResponseCallback();
  const { setCoachConversation } = useSetCoachConversation();
  const createResourceButtonOnClickHandlers = useExternalResourceButtonOnClickHandlers;

  const onClickAppointmentTile = useCallback(
    (appointmentNode: SchedulePreviewAppointment, appointmentPosition: number) => {
      const appointment = appointmentNode.node;
      openClientInfoDrawerForAppointment(appointment);
      userClicked(EventTypeEnum.WAITING_ROOM_SCHEDULE, {
        appointmentPosition,
        appointmentShortId: appointment?.appointmentShortId,
      });
    },
    [openClientInfoDrawerForAppointment, userClicked],
  );

  const onClickUnreadCoachConversation = useCallback(
    (appointment: SchedulePreviewAppointment['node'], conversation: CoachConversation) => {
      openClientInfoDrawerForAppointment(appointment);
      setCoachConversation(conversation);
    },
    [openClientInfoDrawerForAppointment, setCoachConversation],
  );

  const preVisitSummaryBetweenNudgesAndContent = useCallback(
    ({
      appointmentShortId,
      appointment,
    }: {
      appointmentShortId: string;
      appointment: SchedulePreviewAppointment['node'];
    }) =>
      appointment.appointmentType === AppointmentType.Intake ? (
        <IntakeSummaryConnectedWithFormsPrefetch
          patientShortId={appointment.patient.shortId}
          appointmentShortId={appointmentShortId}
          showViewFullIntakeButton
          source={INTAKE_SUMMARY_SOURCE.TELEHEALTH_AGENDA}
          isInverse
          onViewFullIntake={() => {
            void openIntakeFormResponse({ appointment });
          }}
        />
      ) : undefined,
    [openIntakeFormResponse],
  );

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <UpcomingAgendaErrorCard
          cardClassName='max-h-[80%] sm:w-[40rem] w-full flex flex-col overflow-auto'
          isInverse={true}
        />
      )}
    >
      <UpcomingAgendaCardConnected
        onClickAppointmentTile={onClickAppointmentTile}
        preVisitSummaryProviderViewEventType={
          ProviderEventType.VIEWED_PRE_VISIT_SUMMARY_NO_MEDICAL_RECORD_UPCOMING_APPOINTMENT_TELEHEALTH
        }
        cardClassName='max-h-[80%] sm:w-[40rem] w-full flex flex-col overflow-auto rebrand:p-450 rebrand:rounded-425-450'
        preVisitSummaryNudgeContext='telehealth-waiting-room'
        preVisitSummaryCardComponent={PreVisitSummaryCardNudgeWrapper}
        preVisitSummaryOnClickUnreadCoachConversation={onClickUnreadCoachConversation}
        preVisitSummaryBetweenNudgesAndContent={preVisitSummaryBetweenNudgesAndContent}
        createResourceButtonOnClickHandlers={createResourceButtonOnClickHandlers}
        enableKaiserIntakeCssrsAgendaHint={!!kaiserGate}
        isInverse={true}
      />
    </ErrorBoundary>
  );
}
